import { z } from "zod";
import type { AuditEntry, BillingStatus } from "./api.js";
import type {
  BilledTo,
  BillingState,
  ConnectionStatus,
  Language,
  PartnerInvoiceStatus,
  PartnerLinkStatus,
  PartnerRole,
  Role,
  SubscriptionStatus,
} from "./enums.js";
import { BILLED_TO, PARTNER_ROLES, ROLES } from "./enums.js";

/* IT partner portal (issue #27). */

/** Identities of partner users carry this tenant id (object id = partner user id). */
export const PARTNER_TENANT_ID = "kontaktfix-partner";

export type PartnerMe = {
  user: {
    id: string;
    name: string;
    email: string;
    role: PartnerRole;
    language: Language;
    twoFactorEnabled: boolean;
    hasPasskey: boolean;
  };
  partner: { id: string; name: string; code: string };
};

export type PartnerCustomer = {
  orgId: string;
  name: string;
  tenantId: string;
  status: PartnerLinkStatus;
  connectionStatus: ConnectionStatus;
  billing: BillingStatus;
  /** The signed-in user's role in this customer; null: no access (technician without assignment). */
  myRole: Role | null;
  linkedAt: string;
};

/* Dashboard (issue #27, part 2). */

export type PartnerDashboardCustomer = {
  orgId: string;
  name: string;
  status: PartnerLinkStatus;
  connectionStatus: ConnectionStatus;
  billingState: BillingState;
  billedTo: BilledTo;
  /** People who receive contacts now (the billable number). */
  people: number;
  contacts: number;
  lastSync: { status: "running" | "done" | "failed" | "cancelled"; at: string; errors: number } | null;
  myRole: Role | null;
};

export type PartnerDashboard = {
  totals: {
    customers: number;
    pending: number;
    connected: number;
    people: number;
    contacts: number;
    /** Customers with a connection problem, a failed last sync or a billing problem. */
    attention: number;
    /** Partner admins only: monthly amount for customers billed to the partner (excl. VAT). */
    partnerMonthly: number | null;
  };
  currency: string;
  customers: PartnerDashboardCustomer[];
};

/** Team activity: what the partner's people changed in its customers (from the customers' history). */
export type PartnerActivityEntry = AuditEntry & { orgId: string; orgName: string };

/* Billing (issue #27, part 3). */

export type PartnerInvoiceDetails = {
  email: string;
  line1: string;
  postalCode: string;
  city: string;
  country: string;
};

export const partnerInvoiceSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  line1: z.string().trim().min(2).max(200),
  postalCode: z.string().trim().min(2).max(20),
  city: z.string().trim().min(1).max(100),
  country: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{2}$/, "invalid_country"),
});

export const partnerBilledToSchema = z.object({ billedTo: z.enum(BILLED_TO) });

export type PartnerBillingCustomer = {
  orgId: string;
  name: string;
  /** False: the link ended and the partner still pays until partnerBillingEndsAt. */
  linked: boolean;
  billedTo: BilledTo;
  /** Partner billing ended: the partner still covers the paid period until this date. */
  partnerBillingEndsAt: string | null;
  billingState: BillingState;
  /** The customer has its own running subscription (it ends at its period end after a switch). */
  ownSubscription: { status: SubscriptionStatus; endsAt: string | null; cancelAtPeriodEnd: boolean } | null;
  trialEndsAt: string | null;
  people: number;
  /** What the partner pays per month for this customer now (0 when the customer pays). */
  monthly: number;
};

export type PartnerBilling = {
  /** Card or other payment method stored at Stripe (Checkout in setup mode). */
  paymentMethodReady: boolean;
  invoice: {
    status: PartnerInvoiceStatus;
    requestedAt: string | null;
    details: PartnerInvoiceDetails | null;
  };
  subscription: { status: SubscriptionStatus; currentPeriodEnd: string | null } | null;
  customers: PartnerBillingCustomer[];
  pricePerUser: number;
  currency: string;
  trialDays: number;
  invoiceDays: number;
  totalMonthly: number;
  checkoutAvailable: boolean;
  portalAvailable: boolean;
};

/** One customer line of a monthly partner report. */
export type PartnerReportRow = {
  orgId: string;
  name: string;
  tenantId: string;
  billedTo: BilledTo;
  people: number;
  contacts: number;
  syncs: number;
  failedSyncs: number;
  /** Monthly amount the partner pays for this customer (0 when the customer pays). */
  amount: number;
};

export type PartnerReport = {
  /** YYYY-MM */
  month: string;
  createdAt: string;
  customers: number;
  total: number;
  currency: string;
};

export type PartnerTeamMember = {
  id: string;
  name: string;
  email: string;
  role: PartnerRole;
  twoFactorEnabled: boolean;
  /** Customer access of technicians (partner admins are admin everywhere). */
  access: { orgId: string; role: Role }[];
};

export type PartnerInvitation = { id: string; email: string; role: PartnerRole; expiresAt: string };

export type PartnerTeam = { members: PartnerTeamMember[]; invitations: PartnerInvitation[] };

/** A customer domain such as muster.ch or muster.onmicrosoft.com. */
const domain = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^(?=.{3,253}$)([a-z0-9-]+\.)+[a-z]{2,}$/, "invalid_domain");

export const partnerCustomerCreateSchema = z.object({
  name: z.string().trim().min(2).max(120),
  domain,
  language: z.enum(["de", "en"]).default("de"),
});

export const partnerInviteSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  role: z.enum(PARTNER_ROLES),
  /** Language of the invitation email; default: the inviter's. */
  language: z.enum(["de", "en"]).optional(),
});

export const partnerRoleSchema = z.object({ role: z.enum(PARTNER_ROLES) });

/** null removes the technician's access to the customer. */
export const partnerAccessSchema = z.object({ orgId: z.string().uuid(), role: z.enum(ROLES).nullable() });

/* Customer side: the partners of an organization. */

export type OrgPartner = {
  id: string;
  partnerName: string;
  status: PartnerLinkStatus;
  createdAt: string;
  approvedAt: string | null;
};

export const partnerLinkSchema = z.object({
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9]{8}$/, "invalid_code"),
});
